import { Project, StatusTypes } from 'models'
import { PaginationSearchBase } from 'store/PaginationSearchBase'
import { TriggerSearchFunc } from 'store/PaginationSearchBase/PaginationSearchBase.types'
import { PROJECT_STORE } from 'store/ProjectStore'
import { RootStore } from 'store/RootStore'
import { Api, getSortingResult, isProjectPublished } from 'utilities'
import { projectsTableInitialPaginationProps, projectsTableInitialSearchProps, projectsTableNeedTimeoutProps, ProjectsTableSearchProps, ProjectsTableSortProps } from './ProjectsTableStore.types'
import { GENERAL_STORE } from 'store/GeneralStore'

export const PROJECTS_TABLE_STORE = 'PROJECTS_TABLE_STORE'

export class ProjectsTableStore extends PaginationSearchBase<
  ProjectsTableSortProps,
  ProjectsTableSearchProps,
  Project
> {
  private _rootStore: RootStore

  constructor (rootStore: RootStore) {
    super(
      projectsTableInitialPaginationProps,
      projectsTableInitialSearchProps,
      [],
      projectsTableNeedTimeoutProps
    )

    this._rootStore = rootStore
  }

  triggerSearch: TriggerSearchFunc = (options) => {
    options?.forceClearResultsFirst && this.setSearchResults([])
    const { projectsList } = this._rootStore[PROJECT_STORE]

    const {
      name,
      type,
      addedAtStart,
      addedAtEnd,
      tags,
      genre,
      status,
      published
    } = this.searchProps

    const applyFilters = (project: Project): boolean => {
      const nameFilter = name
        ? project.name.toLowerCase().includes(name.trim().toLowerCase())
        : true

      const typeFilter = type
        ? project.type === type
        : true

      const addedAtFilter = addedAtStart
        ? project.createdDate.isBetween(addedAtStart, addedAtEnd, undefined, '[]')
        : true

      const tagsFilter = tags
        ? (project.tags || '').toLowerCase().includes(tags.trim().toLowerCase())
        : true

      const genreFilter = genre
        ? project.genresAsString.includes(genre)
        : true

      const statusFilter = status
        ? project.status === status || status === StatusTypes.PUBLISHED && project.status === StatusTypes.HAS_CHANGES
        : true

      const publishedFilter = published as unknown !== ''
        ? isProjectPublished(project.status) === published
        : true

      return (
        nameFilter &&
        typeFilter &&
        addedAtFilter &&
        tagsFilter &&
        genreFilter &&
        statusFilter &&
        publishedFilter
      ) || (project.children || []).some(applyFilters)
    }

    const results = projectsList.filter(applyFilters)

    const { sortProp, sortDirection } = this.pagination
    results.sort((a, b) => {
      let valueA, valueB

      switch (sortProp) {
        case ProjectsTableSortProps.NAME: {
          valueA = a.name
          valueB = b.name
          break
        }
        case ProjectsTableSortProps.ADDED_AT: {
          valueA = a.createdDate
          valueB = b.createdDate
          break
        }
        case ProjectsTableSortProps.GENRE: {
          valueA = a.genresAsString
          valueB = b.genresAsString
          break
        }
        case ProjectsTableSortProps.TYPE: {
          valueA = a.type
          valueB = b.type
          break
        }
        case ProjectsTableSortProps.INPUT_TOKEN_USED: {
          valueA = a.inputGeminiTokens || 0
          valueB = b.inputGeminiTokens || 0
          break
        }
        case ProjectsTableSortProps.OUTPUT_TOKEN_USED: {
          valueA = a.outputGeminiTokens || 0
          valueB = b.outputGeminiTokens || 0
          break
        }
        default: break
      }

      return getSortingResult(valueA, valueB, sortDirection)
    })

    this.setSearchResults(results)
  }

  /** Delete project by Id */
  deleteProjectById = (projectId: string | number) => {
    const {
      setLoadingOn,
      setLoadingOff,
      handleApiError,
      showSuccess
    } = this._rootStore[GENERAL_STORE]

    setLoadingOn()
    Api
      .delete(`/project/${projectId}/delete`, 'mainApi')
      .then((_) => {
        this.setSearchResults(this.searchResults.filter((p: Project) => p?.id !== projectId))
        showSuccess('Project has been deleted.')
        setLoadingOff()
      })
      .catch(handleApiError)
  }
}
